<template>
  <div class="fts-file" v-if="visibility">
    <!-- multiple -->
    <div class="file-container" v-if="multiple">
      <div class="file-wrapper" v-for="x in data" :key="x.id">
        <fts-file-content 
          :file-name="x.name" 
          :file-path="filePath(x)" 
          :file-type="fileType" 
          :file-content-class="fileContentClass"
          :file-active="x.active"
          :file-progress="x.progress"
          :file-size="x.size"
          :image-width="imageWidth"
          :image-height="imageHeight"
          :editable="editable"
          :cancellable="cancellable"
          :downloadable="downloadable"
          :image-contain="imageContain"
          @removeFile="removeFile(x)"
          @openLightbox="openLightbox(x)" 
          @cancelUpload="cancelUpload(x)"
        />
      </div>     
    </div>
    <!-- single -->
    <fts-file-content 
      :file-name="data.name" 
      :file-path="filePath(data)" 
      :file-type="fileType" 
      :file-content-class="fileContentClass"
      :file-progress="data.progress"
      :file-active="data.active"
      :file-size="data.size"
      :image-width="imageWidth"
      :image-height="imageHeight"
      :editable="editable"
      :cancellable="cancellable"
      :downloadable="downloadable"
      :image-contain="imageContain"
      @removeFile="removeFile(data)" 
      @openLightbox="openLightbox(data)"
      @cancelUpload="cancelUpload(data)"
      v-else 
    />
    <!-- Lightbox -->
    <fts-modal 
      class="lightbox-modal" 
      append-to-body
      full-screen
      header-class="lightbox-header"
      content-class="lightbox-content"
      append-container-class="lightbox-modal-container"
      :footer="false"
      @close="closeLightbox" 
      @keydown.native.left.prevent="lightboxNavLeft"
      @keydown.native.right.prevent="lightboxNavRight"
      v-if="lightboxVisibility"
      ref="lightboxModal">
      <template slot="header">
        <div class="lightbox-nav-left"> 
          <div class="lightbox-nav-pagination">
            {{lightboxSelectedIdx + 1}} / {{lightboxContent.length}}
          </div>
        </div>
        <div class="lightbox-nav-right">
          <div class="lightbox-nav-actions">
            <div class="lightbox-nav-action" v-if="lightboxFullscreen"> 
              <font-awesome-icon
                :icon="['fas', 'compress']"
                fixed-width
                @click="compressLightboxFullscreen"
              />
            </div>
            <div class="lightbox-nav-action" v-else>
              <font-awesome-icon
                :icon="['fas', 'expand']"
                fixed-width
                @click="expandLightboxFullscreen"
              />
            </div>
            <a 
              class="lightbox-nav-action" 
              :href="`${filePath(lightboxContent[lightboxSelectedIdx])}?download=true`"
              target="_blank"
              v-if="downloadable">
              <font-awesome-icon
                :icon="['fas', 'cloud-download-alt']" 
                fixed-width 
              />
            </a>
            <div class="lightbox-nav-action">
              <font-awesome-icon
                :icon="['fas', 'times']"
                fixed-width
                @click="closeLightbox"
              />
            </div>
          </div>
        </div>
      </template>
      <template slot="body">
        <!-- multiple -->
        <template v-if="lightboxContent && lightboxContent.length > 1">
          <div class="lightbox-action-l" @click="lightboxNavLeft">
            <div class="lightbox-action">
              <font-awesome-icon :icon="['fas', 'angle-left']" />
            </div>
          </div>
          <div class="lightbox-action-r" @click="lightboxNavRight">
            <div class="lightbox-action">
              <font-awesome-icon :icon="['fas', 'angle-right']" />
            </div>
          </div>
        </template>      
        <!-- single -->
        <div class="lightbox-image-wrapper">
          <img :src="filePath(lightboxContent[lightboxSelectedIdx])" />
        </div>
        <!-- description -->
        <div class="lightbox-description" v-if="description">
          <div class="lightbox-description-wrapper">
            <div class="lightbox-description-audit">
              <fts-profile 
                :user-name="audit.userName" 
                :profile-photo-size="25" 
                profileNameClass="lightbox-description-audit__name"
                v-if="audit.userName"
              />
              <div class="lightbox-description-audit__date">{{moment(audit.date).format('llll')}}</div>
            </div>
            <div
              class="lightbox-description-content fts-scrollbar fts-contenteditable-display" 
              v-html="description"
            />
          </div>
        </div>
        <!-- content footer -->
        <div class="lightbox-content-footer" v-else></div>
      </template>
    </fts-modal>
  </div>
</template>

<script>
import lodash from "lodash";
import moment from "moment";

import webAppConfig from "../../commons/webApp.config"
import commons from "../../commons/commons";

import FTSFileContent from "./FTSFileContent";
import FTSModal from "../FTSModal";
import FTSProfile from "../FTSProfile";
import FTSTippy from "../FTSTippy";

export default {
  props: {
    moduleId: [ String, Number ],
    prefixURL: String, 
    data: {
      type: [ Array, Object ],
      default: () => ({})
    },
    fileType: String,
    fileContentClass: String,
    imageWidth: Number,
    imageHeight: Number,
    imageContain: Boolean,
    editable: Boolean,
    cancellable: Boolean,
    downloadable: Boolean,
    audit: {
      type: Object,
      default: () => ({
        userName: null,
        date: null
      })
    },
    description: [String, Number]
  },
  components: {
    "fts-file-content": FTSFileContent,
    "fts-modal": FTSModal,
    "fts-profile": FTSProfile,
    "fts-tippy": FTSTippy
  },
  data: () => ({
    lightboxVisibility: false,
    lightboxContent: [],
    lightboxSelectedIdx: null,
    lightboxDesc: null,
    lightboxFullscreen: false,
  }),
  methods: {
    moment,
    filePath(file) {
      let path = file.path || file.blob;
      if (path) {
        if (path.startsWith('/s/')) {
          const url = path.split('/');
          const filename = encodeURIComponent(url.pop());
          const encodeURL = `/api/d/${this.prefixURL}/${url.join("/")}/${filename}`;
          return webAppConfig.baseURL ? 
            `${webAppConfig.baseURL}${encodeURL}` : encodeURL;
        }
        else {
          const url = path.split('/');
          const filename = encodeURIComponent(url.pop());
          const encodeURL = `${url.join("/")}/${filename}`;
          return webAppConfig.baseURL && !path.startsWith('blob:') ? 
            `${webAppConfig.baseURL}/${encodeURL}` : encodeURL;
        }
      }
    },
    removeFile(file) {
      if (this.editable)
        this.$emit("removeFile", file)
    },
    lightboxFullscreenHandler() {
      if (
        (
          document.fullScreen ||
          document.mozFullScreen ||
          document.webkitIsFullScreen ||
          (document.msFullscreenElement != null) 
        ) && this.lightboxModal === document.fullscreenElement
      )
        this.lightboxFullscreen = true;
      else
        this.lightboxFullscreen = false;
    },
    openLightbox(file) {
      let res = this.multiple ? this.data : [this.data];
      this.lightboxContent = res.filter(x => commons.isImageFile(x.name));
      this.lightboxSelectedIdx = lodash.findIndex(this.lightboxContent, file);
      this.lightboxVisibility = true;
      this.$nextTick(() => {
        this.lightboxModal = this.$refs.lightboxModal && this.$refs.lightboxModal.$el;
        this.lightboxModal.addEventListener('fullscreenchange', this.lightboxFullscreenHandler);
      });
    },
    closeLightbox() {
      this.lightboxModal.removeEventListener('fullscreenchange', this.lightboxFullscreenHandler);
      this.lightboxVisibility = false;
      /* append to body */
      this.lightboxModal && document.body.removeChild(this.lightboxModal);
    },
    expandLightboxFullscreen() {
      try {
        const el = this.$refs.lightboxModal.$el;
        if (el.requestFullscreen)
          el.requestFullscreen();
        else if (el.webkitRequestFullscreen)
          el.webkitRequestFullscreen();
        else if (el.msRequestFullscreen)
          el.msRequestFullscreen();
        else
          throw "browser unsupported";
      } catch(err) {
        console.warn('File:ExpandLightboxFullscreen', err);
      }
    },
    compressLightboxFullscreen() {
      try {
        if (document.exitFullscreen)
          document.exitFullscreen();
        else if (document.webkitExitFullscreen)
          document.webkitExitFullscreen();
        else if (document.msExitFullscreen)
          document.msExitFullscreen();
        else
          throw "browser unsupported";
      } catch(err) {
        console.warn('File:CompressLightboxFullscreen', err);
      }
    },
    lightboxNavLeft() {
      if (this.lightboxSelectedIdx - 1 < 0) 
        this.lightboxSelectedIdx = this.lightboxContent.length - 1
      else 
        this.lightboxSelectedIdx -= 1
    },
    lightboxNavRight() {
      if (this.lightboxSelectedIdx + 1 >= this.lightboxContent.length)
        this.lightboxSelectedIdx = 0
      else
        this.lightboxSelectedIdx += 1
    },
    cancelUpload(file) {
      this.$emit("cancelUpload", file)
    },
  },
  computed: {
    visibility() {
      return this.data && (this.multiple && this.data.length || Object.keys(this.data).length)
    },
    multiple() {
      return Array.isArray(this.data)
    },
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-file {
  .file-container {
    @include flex-gap($gap: .135rem);
  }
}

.lightbox-modal {
  .lightbox-modal-container {
    background: var(--fts-lightbox-background) !important;
  }
  .lightbox-header {
    height: 55px;
    display: flex;
    align-items: center;
    .lightbox-nav-left {
      min-width: 0;
      flex: 1;
      .lightbox-nav-pagination {
        padding: 0 1rem;
        font-size: 1rem;
        font-weight: 300;
        color: var(--fts-gray-5);
      }
    }
    .lightbox-nav-right {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding: 0 1rem;
      .lightbox-nav-actions {
        display: inherit;
        align-items: inherit;
        margin-left: -0.75rem;
        margin-right: -0.75rem;    
        .lightbox-nav-action {
          &:not(:last-child) {
            margin-right: .25rem;
          }
          padding: .75rem;
          font-size: 1.25rem;
          color: var(--fts-gray-5);
          cursor: pointer;
          > svg {
            font-size: inherit;
            color: inherit;
          } 
        }
      }
    }
  }
  .lightbox-content {
    @include flex-center-middle;
    flex-direction: column;
    position: relative;
    @mixin lightbox-action($dir) {
      @if ($dir == left) {
        left: 0;
        margin-left: calc(1rem - 0.75rem);
      } @else if ($dir == right) {
        right: 0;
        margin-right: calc(1rem - 0.75rem);
      }
      @include user-select-none;
      position: absolute;
      top: 0;
      bottom: 0;
      padding: .75rem;    
      display: flex;
      align-items: center;
      cursor: pointer;
      .lightbox-action {
        @include flex-center-middle;
        height: 40px;
        width: 40px;
        border-radius: .375rem;
        background: rgba($color: #000000, $alpha: .3);
        color: #fff;
        > svg {
          font-size: 1.25rem;
          color: inherit;
        }
      }
    }
    .lightbox-image-wrapper {
      @include flex-center-middle;
      @include user-select-none;
      flex: 1;
      max-width: 100%;
      overflow: hidden;
      > img {
        width: auto;
        height: auto;
        max-width: 100%;
        max-height: 100%;
      }
    }
    .lightbox-description {
      max-width: 100%;
      margin-right: auto;
      padding: 1rem;
      .lightbox-description-wrapper {
        @include theme() {
          background: rgba(theme-get('fts-gray-5'), .095);
        }
        padding: .75rem 1rem;
        border-radius: .375rem;
        .lightbox-description-audit {
          display: flex;
          align-items: center;
          margin-bottom: .75rem;
          .lightbox-description-audit__name {
            font-size: .875rem;
            color: $primaryColor;
          }
          .lightbox-description-audit__date {
            margin-left: .375rem;
            font-size: .675rem;
            color: var(--fts-gray-5);
          }
        }
        .lightbox-description-content {
          max-height: 150px;
          font-size: .875rem;
          color: #eee;
        }
      }
    }
    .lightbox-action-l {
      @include lightbox-action(left);
    }
    .lightbox-action-r {
      @include lightbox-action(right)
    }
    .lightbox-content-footer {
      height: 55px;
    }
  }
}
</style>